import { FormSectionHeader } from "@/components";
import { Button, Card, CardContent } from "@/components/ui";
import useReportSection from "./useReportSection";
import { ChevronRight, FileChartColumnIncreasing, MoveRight, ReceiptText } from "lucide-react";
import { cn } from "@/lib/utils";

const ReportSection = ({ variant = "quick" }: { variant?: "quick" | "owner" }) => {
  const { onReportCardClick, reportCards } =
    useReportSection();

  const renderOwnerCards = (section: "collection" | "invoice") => {
    return reportCards?.filter((data: any) => data.section == section)?.map((data: any) => {
      const Icon = data?.icon;
      return (
        <Card
          className={cn(
            "cursor-pointer shadow-none p-0 border",
            data?.className
          )}
          onClick={onReportCardClick(data?.link)}
        >
          <CardContent className="flex min-h-[64px] flex-col gap-3 p-2">
            <div className="flex flex-row items-center gap-2">
              <div className={cn(data?.iconClassName, "flex items-center rounded-full bg-white h-[40px] w-[40px] justify-center")}>
                {Icon ? (
                  <Icon className="h-[20px] w-[20px]" />
                ) : (
                  <ReceiptText className="h-[20px] w-[20px]" />
                )}
              </div>
              <span className="text-[14px] font-[600] leading-5 text-gray-900">
                {data?.title}
              </span>
            </div>
            <div className="flex w-full flex-col items-start">
              <span className="text-[12px] font-normal text-gray-500 my-1">
                {data?.description}
              </span>
              <div className={cn("text-[12px] flex flex-row items-center gap-2", data?.textClassName)}>
                View Report <MoveRight className="w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
      );
    })
  }

  if (variant == "owner") {
    return (
      <Card className="shadow-none">
        <CardContent>
          <FormSectionHeader className="text-[18px] font-[500] text-gray-900">
            Reports
          </FormSectionHeader>
          <div className="flex flex-col gap-5">
            <Card className={cn("bg-gray-50 shadow-none")}>
              <CardContent>
                <FormSectionHeader className="text-[16px] font-[500] text-gray-900 flex flex-col">
                  <span>Invoice Reports</span>
                  <span className="text-gray-400 text-[12px]">Access all invoice related reports and insights</span>
                </FormSectionHeader>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {renderOwnerCards("invoice")}
                </div>
              </CardContent>
            </Card>
            <Card className={cn("bg-gray-50 shadow-none")}>
              <CardContent>
                <FormSectionHeader className="text-[16px] font-[500] text-gray-900 flex flex-col">
                  <span>Collection Reports</span>
                  <span className="text-gray-400 text-[12px]">Access all collection related reports and insights</span>
                </FormSectionHeader>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {renderOwnerCards("collection")}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-none">
      <CardContent>
        <FormSectionHeader className="text-[18px] font-[500] flex flex-row items-center gap-2 text-primary">
          <FileChartColumnIncreasing className="h-6 w-6 stroke-2" /> REPORTS (QUICK ACCESS)
        </FormSectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {reportCards?.map((data: any) => {
            const Icon = data.icon;
            return (
              <Card className="shadow-none bg-blue-50 p-0">
                <CardContent className="flex flex-row items-center justify-center gap-3 p-3">
                  <div className="flex items-center rounded-full p-3 bg-primary">
                    <Icon className="stroke-white" />
                  </div>
                  <div className="flex flex-col w-full items-start">
                    <span className="text-[16px] font-[500] text-primary">
                      {data?.title || ""}
                    </span>
                    <Button variant="link" className="p-0 m-0 -ml-3 text-black no-underline" onClick={onReportCardClick(data?.link)}>
                      View Report <ChevronRight />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportSection;
